import { osnovaApi } from '../base';
import type { DispatcherForward, MailboxRoute, MailboxRouteInput } from '../types';

/** Справочники и правила кодирования корреспонденции ООО «Полюс Сухой Лог». */
export interface PolyusRules {
  projects: Array<{ code: string; name: string }>;
  disciplines: Array<{ code: string; name: string }>;
  /** Код отправителя САТКОМ (присвоен Заказчиком). */
  sender_code: string;
  recipient_code: string;
  official_email: string;
  vdr_access_email: string;
  max_attachment_mb: number;
  /** Номер, который получит следующий шифр. */
  next_seq: number;
  effective_from: string;
  /** Отправка писем из ОСНОВЫ: с какого адреса и на какой. */
  sending: {
    /** false — почта на сервере не настроена, отправить нельзя. */
    enabled: boolean;
    from_email: string | null;
    from_name: string;
    to: string;
  };
}

/** Проверка PDF письма до отправки. */
export interface PolyusLetterCheck {
  /** Файл — PDF. */
  pdf: boolean;
  /** Текст PDF удалось прочитать (false — сервис разбора недоступен). */
  checked?: boolean;
  size_bytes: number;
  pages?: number;
  /** В PDF есть текст (false — скан без текстового слоя). */
  text_found?: boolean;
  /** На бланке стоит именно этот шифр. */
  code_found?: boolean;
  /** Другие шифры САТКОМ в письме (опечатка или чужой шифр). */
  other_codes?: string[];
}

/** Запись журнала: выданный шифр письма САТКОМ Заказчику. */
export interface PolyusCodeEntry {
  id: string;
  code: string;
  project: string;
  project_name: string | null;
  sender_code: string;
  recipient_code: string;
  discipline: string;
  discipline_name: string | null;
  seq: number;
  subject: string;
  reply_to: string | null;
  outgoing_number: string | null;
  /** active — выдан, письмо ещё не ушло; sending — отправляется; sent — отправлено. */
  status: 'active' | 'sending' | 'sent' | 'cancelled';
  cancel_reason: string | null;
  created_by: string | null;
  created_at: string;
  cancelled_by: string | null;
  cancelled_at: string | null;
  /** osnova — выдан ОСНОВОЙ; mailbox — найден в «Отправленных»; manual — внесён вручную. */
  source: 'osnova' | 'mailbox' | 'manual';
  sent_at: string | null;
  sent_from: string | null;
  sent_to: string | null;
  correspondence_id: string | null;
  vdr_link: string | null;
  /** Почему не удалась последняя отправка из ОСНОВЫ. */
  send_error: string | null;
  /** Имя файла письма по правилам Заказчика. */
  file_name: string;
  /** Как оформить письмо (только в ответе на выдачу шифра). */
  guidance?: string[];
  /** Замечания после отправки (письмо ушло, но что-то не записалось). */
  warnings?: string[];
  /** Внесение вручную: false — шифр уже был в журнале. */
  created?: boolean;
}

/** Шифр ПОЛЮС, найденный в письме. */
export interface PolyusFoundCode {
  code: string;
  project: string;
  project_name: string | null;
  sender: string;
  recipient: string;
  discipline: string;
  discipline_name: string | null;
  seq: number;
  /** Письмо Заказчика (отправитель SL). */
  from_customer: boolean;
  /** Что в шифре не так по правилам Заказчика. */
  problems: string[];
  /** Запись журнала, если шифр выдан ОСНОВОЙ. */
  journal: {
    id: string;
    subject: string;
    status: 'active' | 'sending' | 'sent' | 'cancelled';
  } | null;
}

/** Шифры ПОЛЮС в письме и замечания к исходящему письму Заказчику. */
export interface PolyusLetterInfo {
  codes: PolyusFoundCode[];
  outgoing_to_customer: boolean;
  issues: string[];
}
import type { Correspondence, DispatcherForwardTarget, MailboxCategory, MailboxCategoryInput } from '@entities/legal/types';

export const dispatcherApi = osnovaApi.injectEndpoints({
  endpoints: (b) => ({
    // ===== Агент-диспетчер: inbox + HITL =====
    listDispatcherInbox: b.query<
      {
        items: Array<Correspondence & { is_unread?: boolean; polyus_code?: string | null }>;
        total: number;
        limit: number;
        offset: number;
      },
      {
        status?: string;
        /** Поиск по отправителю / теме / содержанию письма (ILIKE %q%). */
        search?: string;
        /** Фильтр по классу: spam | pretension | gov_request | court_doc |
         *  contract_draft | corporate | bankruptcy | notification | other. */
        letter_type?: string;
        /** Направление: 'incoming' (по умолчанию) | 'outgoing'. */
        direction?: 'incoming' | 'outgoing';
        /** Фильтр по ящику-источнику (correspondence.source_mailbox).
         *  Пустая строка / undefined — фильтр не применяется (все ящики). */
        mailbox?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/dispatcher/inbox',
        params: p ?? undefined,
      }),
      providesTags: ['DispatcherInbox'],
    }),
    /** Агрегация входящих по ящику-источнику для переключателя ящиков.
     *  Ключ mailbox === '' — legacy-письма без source_mailbox (UI: «Основной»). */
    listDispatcherMailboxes: b.query<
      Array<{ mailbox: string; total: number; unread: number }>,
      void
    >({
      query: () => '/agents/statut/dispatcher/mailboxes',
      providesTags: ['DispatcherInbox'],
    }),
    markDispatcherEmailRead: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/dispatcher/inbox/${id}/read`,
        method: 'POST',
      }),
      // Помечает unread → read локально, перезатягиваем список чтобы
      // точки исчезли.
      invalidatesTags: (_r, _e, id) => [
        { type: 'DispatcherInbox', id },
        'DispatcherInbox',
      ],
    }),
    getDispatcherEmail: b.query<
      {
        correspondence: Correspondence;
        attachments: Array<{
          id: string;
          correspondence_id: string;
          filename: string;
          content_type: string | null;
          file_hash: string;
          size_bytes: number;
          created_at: string;
        }>;
        routing_errors: Array<{
          id: string;
          correspondence_id: string;
          expected_department: string | null;
          dispatcher_department: string | null;
          reason: string | null;
          created_at: string;
        }>;
        routed_to: {
          kind: 'claim' | 'contract';
          id: string;
          url: string;
          label: string;
        } | null;
        annotations?: Array<{
          id: string;
          decision: string | null;
          comment: string | null;
          risk_category: string | null;
          context_summary: string;
          is_active: boolean;
          actor_user_id: string | null;
          actor_full_name: string | null;
          actor_email: string | null;
          created_at: string;
          updated_at: string;
        }>;
        /** Связанные исходящие письма этому же контрагенту (наши ответы). */
        related_outgoing?: Correspondence[];
        /** Шифры ПОЛЮС в письме; `null` — письмо не про проект ПОЛЮС. */
        polyus?: PolyusLetterInfo | null;
      },
      string
    >({
      query: (id) => `/agents/statut/dispatcher/inbox/${id}`,
      providesTags: (_r, _e, id) => [{ type: 'DispatcherInbox', id }],
      // Держим карточку письма в кэше 10 минут после того, как все
      // компоненты её отпустили. При переключении inbox → старое письмо
      // открывается мгновенно из кэша, без повторного запроса.
      keepUnusedDataFor: 600,
    }),
    annotateDispatcherRouting: b.mutation<
      {
        ok: boolean;
        routed: boolean;
        kind: string | null;
        target_id: string | null;
        message: string | null;
      },
      {
        id: string;
        decision: 'accepted' | 'disputed' | 'rejected';
        correct_routing?: string;
        comment?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/dispatcher/inbox/${id}/annotate`,
        method: 'POST',
        body,
      }),
      // Реклассификация может создавать claim или contract — обнуляем их
      // реестры тоже, не только Inbox.
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'DispatcherInbox', id },
        'DispatcherInbox',
        'Claim',
        'Contract',
      ],
    }),

    // === Шифры проектной корреспонденции ПОЛЮС ===
    getPolyusRules: b.query<PolyusRules, void>({
      query: () => '/agents/statut/dispatcher/polyus-codes/rules',
      providesTags: ['PolyusCodes'],
    }),
    listPolyusCodes: b.query<
      { items: PolyusCodeEntry[]; total: number },
      { search?: string; limit?: number; offset?: number } | void
    >({
      query: (p) => ({ url: '/agents/statut/dispatcher/polyus-codes', params: p ?? undefined }),
      providesTags: ['PolyusCodes'],
    }),
    assignPolyusCode: b.mutation<
      PolyusCodeEntry,
      {
        project: string;
        discipline: string;
        subject: string;
        reply_to?: string;
        outgoing_number?: string;
      }
    >({
      query: (body) => ({ url: '/agents/statut/dispatcher/polyus-codes', method: 'POST', body }),
      invalidatesTags: ['PolyusCodes', 'DispatcherInbox'],
    }),
    cancelPolyusCode: b.mutation<PolyusCodeEntry, { id: string; reason?: string }>({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/dispatcher/polyus-codes/${id}/cancel`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolyusCodes', 'DispatcherInbox'],
    }),
    /** Проверить PDF письма до отправки (ничего не отправляет). */
    checkPolyusLetter: b.mutation<PolyusLetterCheck, { id: string; form: FormData }>({
      query: ({ id, form }) => ({
        url: `/agents/statut/dispatcher/polyus-codes/${id}/check-letter`,
        method: 'POST',
        body: form,
      }),
    }),
    /** Отправить письмо с шифром Заказчику: letter, attachment[], body, vdr_link. */
    sendPolyusLetter: b.mutation<PolyusCodeEntry, { id: string; form: FormData }>({
      query: ({ id, form }) => ({
        url: `/agents/statut/dispatcher/polyus-codes/${id}/send`,
        method: 'POST',
        body: form,
      }),
      invalidatesTags: ['PolyusCodes', 'DispatcherInbox'],
    }),
    /** Внести шифр письма, ушедшего мимо ОСНОВЫ. */
    registerExternalPolyusCode: b.mutation<
      PolyusCodeEntry,
      { code: string; subject: string; reply_to?: string; outgoing_number?: string }
    >({
      query: (body) => ({
        url: '/agents/statut/dispatcher/polyus-codes/external',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['PolyusCodes', 'DispatcherInbox'],
    }),

    // === Mailbox routing (агент-диспетчер): admin CRUD ===
    listMailboxRoutes: b.query<
      { items: MailboxRoute[]; total: number },
      { include_inactive?: boolean } | void
    >({
      query: (p) => ({ url: '/admin/mailbox-routes', params: p ?? undefined }),
      providesTags: ['MailboxRoute'],
    }),
    createMailboxRoute: b.mutation<MailboxRoute, MailboxRouteInput>({
      query: (body) => ({ url: '/admin/mailbox-routes', method: 'POST', body }),
      invalidatesTags: ['MailboxRoute'],
    }),
    updateMailboxRoute: b.mutation<MailboxRoute, { id: string; input: MailboxRouteInput }>({
      query: ({ id, input }) => ({
        url: `/admin/mailbox-routes/${id}`,
        method: 'PATCH',
        body: input,
      }),
      invalidatesTags: ['MailboxRoute'],
    }),
    deactivateMailboxRoute: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/admin/mailbox-routes/${id}`, method: 'DELETE' }),
      invalidatesTags: ['MailboxRoute'],
    }),
    // === Mailbox categories (Вариант 3): справочник + маппинг category→routes ===
    // Чтение — под require_any_agent_access(dispatcher|statut, read); запись —
    // require_agent_access(dispatcher, write), как у mailbox-routes CRUD.
    listMailboxCategories: b.query<
      { items: MailboxCategory[] },
      { include_inactive?: boolean } | void
    >({
      query: (p) => ({
        url: '/agents/statut/dispatcher/mailbox-categories',
        params: p ?? undefined,
      }),
      providesTags: ['MailboxCategory'],
    }),
    createMailboxCategory: b.mutation<MailboxCategory, MailboxCategoryInput>({
      query: (body) => ({
        url: '/agents/statut/dispatcher/mailbox-categories',
        method: 'POST',
        body,
      }),
      invalidatesTags: ['MailboxCategory'],
    }),
    updateMailboxCategory: b.mutation<
      MailboxCategory,
      { id: string; input: MailboxCategoryInput }
    >({
      query: ({ id, input }) => ({
        url: `/agents/statut/dispatcher/mailbox-categories/${id}`,
        method: 'PATCH',
        body: input,
      }),
      invalidatesTags: ['MailboxCategory'],
    }),
    deleteMailboxCategory: b.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/agents/statut/dispatcher/mailbox-categories/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MailboxCategory'],
    }),
    setMailboxCategoryRoutes: b.mutation<
      MailboxCategory,
      { id: string; route_ids: string[] }
    >({
      query: ({ id, route_ids }) => ({
        url: `/agents/statut/dispatcher/mailbox-categories/${id}/routes`,
        method: 'PUT',
        body: { route_ids },
      }),
      invalidatesTags: ['MailboxCategory'],
    }),
    // === Mailbox routing: статус + HITL + ручная отправка по письму ===
    getDispatcherForward: b.query<
      {
        forward: DispatcherForward;
        route: MailboxRoute | null;
        /** Fan-out цели (миграция 0099). Пусто для писем, обработанных до
         *  включения мульти-адресной пересылки — это норма, не ошибка. */
        targets: DispatcherForwardTarget[];
      },
      string
    >({
      query: (id) => `/agents/statut/dispatcher/inbox/${id}/forwards`,
      providesTags: (_r, _e, id) => [{ type: 'DispatcherForward', id }],
    }),
    annotateDispatcherMailbox: b.mutation<
      { ok: boolean },
      {
        id: string;
        decision: 'correct' | 'wrong';
        correct_route_id?: string;
        correct_email?: string;
        comment?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/dispatcher/inbox/${id}/annotate-mailbox`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'DispatcherForward', id },
        { type: 'DispatcherInbox', id },
        'DispatcherInbox',
      ],
    }),
    forwardDispatcherManual: b.mutation<
      {
        ok: boolean;
        /** Результат по каждой цели мульти-адресной отправки. Адреса,
         *  уже бывшие в копии письма, приходят со status='skipped' и
         *  skip_reason='already_in_cc' — SMTP по ним не дёргался. */
        results: Array<{
          email: string;
          status: DispatcherForwardTarget['status'];
          message_id?: string | null;
          last_error?: string | null;
          skip_reason?: string | null;
        }>;
      },
      {
        id: string;
        /** Мульти-режим: набор целей. Если непуст — игнорируются одиночные
         *  route_id/email. Каждая цель — либо ящик из справочника (route_id),
         *  либо произвольный адрес (email). */
        targets?: Array<{ route_id?: string; email?: string }>;
        /** Fallback одиночного режима (обратная совместимость). */
        route_id?: string;
        email?: string;
        comment?: string;
      }
    >({
      query: ({ id, ...body }) => ({
        url: `/agents/statut/dispatcher/inbox/${id}/forward-manual`,
        method: 'POST',
        body,
      }),
      invalidatesTags: (_r, _e, { id }) => [
        { type: 'DispatcherForward', id },
        { type: 'DispatcherInbox', id },
        'DispatcherInbox',
      ],
    }),

    // Список реально отправленных перенаправлений (вкладка «Отправленные»).
    // Каждый элемент несёт само письмо + запись перенаправления + маршрут
    // (куда отправлено: display_name + email). Без `status` backend вернёт
    // все три «отправленных» статуса (sent | manual | corrected).
    listDispatcherForwards: b.query<
      {
        items: Array<{
          correspondence: Correspondence;
          forward: DispatcherForward;
          route: MailboxRoute | null;
        }>;
        total: number;
        limit: number;
        offset: number;
      },
      {
        status?: 'sent' | 'manual' | 'corrected';
        /** Поиск по теме / отправителю / маршруту (ILIKE %q%). */
        search?: string;
        limit?: number;
        offset?: number;
      } | void
    >({
      query: (p) => ({
        url: '/agents/statut/dispatcher/forwards',
        params: p ?? undefined,
      }),
      providesTags: ['DispatcherForward', 'DispatcherInbox'],
    }),

    // ===================== ОСНОВА.Экономист =====================
    // Прогоны отчётных периодов (bdr | mining | canteen): загрузка
    // источников, извлечение, HITL-разметка аномалий/строк, сборка и
    // выгрузка XLSX-отчёта. Все роуты под RBAC агента `econ`.

  }),
  overrideExisting: false,
});

export const {
  useListDispatcherInboxQuery,
  useListDispatcherMailboxesQuery,
  useGetDispatcherEmailQuery,
  useAnnotateDispatcherRoutingMutation,
  useMarkDispatcherEmailReadMutation,
  useListMailboxRoutesQuery,
  useCreateMailboxRouteMutation,
  useUpdateMailboxRouteMutation,
  useDeactivateMailboxRouteMutation,
  useListMailboxCategoriesQuery,
  useCreateMailboxCategoryMutation,
  useUpdateMailboxCategoryMutation,
  useDeleteMailboxCategoryMutation,
  useSetMailboxCategoryRoutesMutation,
  useGetDispatcherForwardQuery,
  useAnnotateDispatcherMailboxMutation,
  useForwardDispatcherManualMutation,
  useListDispatcherForwardsQuery,
  useGetPolyusRulesQuery,
  useListPolyusCodesQuery,
  useAssignPolyusCodeMutation,
  useCancelPolyusCodeMutation,
  useCheckPolyusLetterMutation,
  useSendPolyusLetterMutation,
  useRegisterExternalPolyusCodeMutation,
} = dispatcherApi;
